/* global window */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, message } from 'antd';
import { connect } from 'react-redux';
import NavbarComponent from '../../Components/Navbar/NavbarComponent';
import DaySchedule from '../../Components/DaySchedule/DayScheduleComponent';
import WeekSchedule from '../../Components/WeekSchedule/WeekScheduleComponent';
import * as eventActions from '../../Actions/EventActions';
import * as settingsActions from '../../Actions/SettingsActions';
import { getEventsById, getEventsByDate, getExternalCalendarSlots } from '../../Reducers/EventReducer';
import { getSettings, getUrls, getSettingsLoadFailed } from '../../Reducers/SettingsReducer';
import { getCalendarUsername } from '../../Utility/inviteLink';
import { browserZone, zoneLabel, formatZonePreview, isSupportedZone, zoneOffsetMinutes, hasStableZoneOffset } from '../../Utility/zonedTime';

const autoAdoptionAttempted = new Set();

const mapStateToProps = state => ({
  eventsById: getEventsById(state),
  eventsByDate: getEventsByDate(state),
  externalCalendarSlots: getExternalCalendarSlots(state),
  settings: getSettings(state),
  urls: getUrls(state),
  settingsLoadFailed: getSettingsLoadFailed(state),
});

const mapDispatchToProps = {
  fetchEvents: eventActions.fetchEvents,
  updateEvent: eventActions.updateEvent,
  fetchBusySlots: eventActions.fetchBusySlots,
  deleteEvent: eventActions.deleteEvent,
  fetchSettings: settingsActions.fetchSettings,
  fetchUrls: settingsActions.fetchUrls,
  setSettings: settingsActions.setSettings,
  createUser: settingsActions.createUser,
};

function visibleClock(stored, fallback, zone) {
  if (!stored) return fallback;
  const clock = moment.utc(stored, 'HH:mm:ss');
  return zone ? clock.add(zoneOffsetMinutes(zone, Date.now()), 'minutes').format('HH:mm') : clock.local().format('HH:mm');
}

class Calendar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      calendarView: 'day',
      date: moment(),
      navbarCssClassName: '',
      zonePromptDismissed: null,
      savingZone: false,
      zoneError: '',
      zoneStatus: '',
      zoneStart: null,
      zoneEnd: null,
      zoneDays: null,
      zoneChoice: null,
      autoAdoption: null,
      openZoneRequest: 0,
    };
    this.autoAdoptionDecision = null;
    this.decideAutoAdoption();
    this.onDateChange = this.onDateChange.bind(this);
    this.onWeekChange = this.onWeekChange.bind(this);
    this.switchToDayView = this.switchToDayView.bind(this);
    this.switchToWeekView = this.switchToWeekView.bind(this);
    this.goToToday = this.goToToday.bind(this);
    this.getSchedule = this.getSchedule.bind(this);
    this.fetchConfigAndEvents = this.fetchConfigAndEvents.bind(this);
    this.handleCalendarScroll = this.handleCalendarScroll.bind(this);
    this.refreshCalendar = this.refreshCalendar.bind(this);
    this.calendarRef = React.createRef();
    this.dismissZonePrompt = this.dismissZonePrompt.bind(this);
    this.useBrowserZone = this.useBrowserZone.bind(this);
    this.expandZonePrompt = this.expandZonePrompt.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
    const startDate = moment()
      .startOf('day')
      .utc()
      .format();
    const endDate = moment()
      .endOf('day')
      .utc()
      .format();
    const timeSlot = {
      start_date: startDate,
      end_date: endDate,
    };
    const { createUser } = this.props;
    createUser(this.fetchConfigAndEvents, timeSlot);
    window.addEventListener('scroll', this.handleCalendarScroll);
    this.maybeAutoAdopt();
  }

  componentDidUpdate(prevProps) {
    const { settings, urls, settingsLoadFailed } = this.props;
    if (prevProps.settings !== settings || prevProps.urls !== urls || prevProps.settingsLoadFailed !== settingsLoadFailed) this.maybeAutoAdopt();
  }

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener('scroll', this.handleCalendarScroll);
  }

  onWeekChange(week) {
    this.setState({
      date: week,
    });
    const { fetchEvents, fetchBusySlots } = this.props;
    const startDate = moment(week)
      .startOf('week')
      .utc()
      .format();
    const endDate = moment(week)
      .endOf('week')
      .utc()
      .format();
    const timeSlot = {
      start_date: startDate,
      end_date: endDate,
    };
    fetchEvents(timeSlot);
    fetchBusySlots(timeSlot);
  }

  onDateChange(date) {
    const { fetchEvents, fetchBusySlots } = this.props;
    if (date) {
      this.setState({
        date,
      });
      const startDate = moment(date)
        .startOf('day')
        .utc()
        .format();
      const endDate = moment(date)
        .endOf('day')
        .utc()
        .format();
      const timeSlot = {
        start_date: startDate,
        end_date: endDate,
      };
      fetchEvents(timeSlot);
      fetchBusySlots(timeSlot);
    }
  }

  getSchedule(calendarView) {
    const { date, scrollTop } = this.state;
    const { eventsById, eventsByDate, updateEvent, deleteEvent, externalCalendarSlots, settings } = this.props;
    switch (calendarView) {
      case 'day':
        return (
          <DaySchedule
            date={date}
            eventsById={eventsById}
            eventsByDate={eventsByDate}
            updateEvent={updateEvent}
            deleteEvent={deleteEvent}
            externalCalendarSlots={externalCalendarSlots}
            settings={settings}
            scrollTop={scrollTop}
          />
        );
      case 'week':
        return (
          <WeekSchedule
            week={date}
            eventsById={eventsById}
            eventsByDate={eventsByDate}
            updateEvent={updateEvent}
            deleteEvent={deleteEvent}
            externalCalendarSlots={externalCalendarSlots}
            settings={settings}
            scrollTop={scrollTop}
          />
        );
      default:
        return (
          <DaySchedule
            date={date}
            eventsById={eventsById}
            eventsByDate={eventsByDate}
            updateEvent={updateEvent}
            externalCalendarSlots={externalCalendarSlots}
            settings={settings}
            scrollTop={scrollTop}
          />
        );
    }
  }

  promptZone(settings) {
    const { settings: currentSettings } = this.props;
    const selectedSettings = settings || currentSettings;
    if (!selectedSettings || !Array.isArray(selectedSettings.TimeZoneIds)) return null;
    const detected = browserZone();
    const zone = selectedSettings.TimeZoneIds.find(id => id.toLowerCase() === (detected || '').toLowerCase() && isSupportedZone(id));
    return zone || null;
  }

  autoAdoptionProposal(props = this.props) {
    const { settings, urls } = props;
    const user = getCalendarUsername();
    const zone = this.promptZone(settings);
    if (!user || autoAdoptionAttempted.has(user) || !zone || /^(UTC|GMT|Etc\/)/i.test(zone) || settings.TimeZone ||
      !Object.keys(urls || {}).some(key => urls[key] && urls[key].Registered) ||
      (!settings.HoursAreDefault && (!settings.StartingFreeTime || !settings.EndingFreeTime)) ||
      !(settings.HoursAreDefault || hasStableZoneOffset(zone))) return null;
    const start = settings.HoursAreDefault ? '09:00' : visibleClock(settings.StartingFreeTime, '09:00', zone);
    const end = settings.HoursAreDefault ? '17:00' : visibleClock(settings.EndingFreeTime, '17:00', zone);
    return start === end ? null : { user, zone, start, end };
  }

  decideAutoAdoption(props = this.props) {
    const { settings, urls, settingsLoadFailed } = props;
    const user = getCalendarUsername();
    if (this.autoAdoptionDecision && this.autoAdoptionDecision.user !== user) this.autoAdoptionDecision = null;
    if (!this.autoAdoptionDecision && user && (Array.isArray(settings.TimeZoneIds) || settingsLoadFailed) && browserZone() && Object.keys(urls || {}).some(key => urls[key] && urls[key].Registered)) {
      this.autoAdoptionDecision = { user, proposal: this.autoAdoptionProposal(props) };
    }
  }

  maybeAutoAdopt() {
    this.decideAutoAdoption();
    const proposal = this.autoAdoptionDecision && this.autoAdoptionDecision.proposal;
    if (!proposal) return;
    const { settings, setSettings, fetchSettings } = this.props;
    const { user, zone, start, end } = proposal;
    if (autoAdoptionAttempted.has(user)) return;
    autoAdoptionAttempted.add(user);
    this.setState({ autoAdoption: { status: 'saving', user } });
    setSettings({
      workingHoursStart: `${start}:00`,
      workingHoursEnd: `${end}:00`,
      availableWeekDays: settings.AvailableWeekDays,
      SlotDuration: settings.SlotDuration,
      BufferDuration: settings.BufferDuration,
      DefaultReminderTime: settings.DefaultReminderTime,
      TimeZoneIds: settings.TimeZoneIds,
      TimeZone: zone,
    }).then(() => this.mounted ? fetchSettings() : null).then(fresh => {
      if (!this.mounted) return;
      if (!fresh || !fresh.TimeZone) throw new Error('Zone missing after save');
      this.setState({ autoAdoption: { status: 'saved', user, zone, start, end }, zoneStatus: `Your working hours now follow ${zoneLabel(zone).split(' – ')[0]}` });
    }).catch(error => {
      // eslint-disable-next-line no-console
      console.error('Automatic time-zone adoption failed', error);
      if (!this.mounted) return;
      this.setState({ autoAdoption: { status: 'failed', user } });
    });
  }

  dismissZonePrompt() {
    try {
      window.localStorage.setItem(`tzAdoptionCollapsed.${getCalendarUsername()}`, 'true');
    } catch (error) { return; }
    this.setState({ zonePromptDismissed: true });
  }

  expandZonePrompt() {
    try {
      window.localStorage.removeItem(`tzAdoptionCollapsed.${getCalendarUsername()}`);
    } catch (error) { /* The in-memory choice still takes precedence. */ }
    this.setState({ zonePromptDismissed: false });
  }

  useBrowserZone() {
    const { zoneChoice, savingZone, zoneStart, zoneEnd, zoneDays } = this.state;
    const zone = zoneChoice || this.promptZone();
    const { settings, setSettings } = this.props;
    if (!zone || !(settings.TimeZoneIds || []).includes(zone) || !isSupportedZone(zone) || savingZone) return;
    const localStart = zoneStart || (settings.HoursAreDefault ? '09:00' : visibleClock(settings.StartingFreeTime, '09:00'));
    const localEnd = zoneEnd || (settings.HoursAreDefault ? '17:00' : visibleClock(settings.EndingFreeTime, '17:00'));
    if (localStart === localEnd) {
      this.setState({ zoneError: "Start and end can't be the same time." });
      return;
    }
    this.setState({ savingZone: true, zoneError: '', zoneConflict: false });
    setSettings({
      workingHoursStart: `${localStart}:00`,
      workingHoursEnd: `${localEnd}:00`,
      availableWeekDays: zoneDays || settings.AvailableWeekDays,
      SlotDuration: settings.SlotDuration,
      BufferDuration: settings.BufferDuration,
      DefaultReminderTime: settings.DefaultReminderTime,
      TimeZoneIds: settings.TimeZoneIds,
      TimeZone: zone,
    }).then(() => {
      this.setState({ savingZone: false, zoneStart: null, zoneEnd: null, zoneDays: null, zoneChoice: null, zoneStatus: `Working hours saved in ${zoneLabel(zone).split(' – ')[0]}` });
      message.success(`Working hours saved in ${zoneLabel(zone).split(' – ')[0]}`);
    }).catch(error => {
      const conflict = error.response && error.response.status === 409 && error.response.data && error.response.data.code === 'zone_set_update_app';
      this.setState({ savingZone: false, zoneError: conflict ? error.response.data.message : "We couldn't save working hours. Please try again." , zoneConflict: conflict });
    });
  }

  fetchConfigAndEvents(timeSlot) {
    const { fetchSettings, fetchEvents, fetchBusySlots, fetchUrls } = this.props;
    fetchSettings();
    fetchUrls();
    fetchEvents(timeSlot);
    fetchBusySlots(timeSlot);
  }

  goToToday() {
    const date = moment();
    const { calendarView } = this.state;
    if (calendarView === 'day') {
      this.onDateChange(date);
    } else {
      this.onWeekChange(date);
    }
  }

  switchToDayView() {
    const { date } = this.state;
    this.setState({
      calendarView: 'day',
      scrollTop: this.calendarRef.current.firstChild.scrollTop,
    });
    this.onDateChange(date);
  }

  switchToWeekView() {
    const { date } = this.state;
    this.setState({
      calendarView: 'week',
      scrollTop: this.calendarRef.current.firstChild.scrollTop,
    });
    this.onWeekChange(date);
  }

  refreshCalendar() {
    const { calendarView, date } = this.state;
    if (calendarView === 'day') {
      this.onDateChange(date);
    } else {
      this.onWeekChange(date);
    }
  }

  handleCalendarScroll() {
    if (this.calendarRef.current.firstChild.scrollTop > 0) {
      this.setState({
        navbarCssClassName: 'navbar-shadow',
      });
    } else {
      this.setState({
        navbarCssClassName: '',
      });
    }
  }

  render() {
    const { calendarView, date, navbarCssClassName, savingZone, zoneError, zoneConflict, zonePromptDismissed, zoneStatus, zoneStart, zoneEnd, zoneDays, zoneChoice, openZoneRequest, autoAdoption: adoptionState } = this.state;
    const autoAdoption = adoptionState && adoptionState.user === getCalendarUsername() ? adoptionState : null;
    const willAutoAdopt = !autoAdoption && this.autoAdoptionDecision && this.autoAdoptionDecision.proposal && !autoAdoptionAttempted.has(getCalendarUsername());
    const zone = this.promptZone();
    let collapsed = zonePromptDismissed === true;
    if (zonePromptDismissed === null) {
      try { collapsed = !!window.localStorage.getItem(`tzAdoptionCollapsed.${getCalendarUsername()}`); } catch (error) { collapsed = false; }
    }
    const { settings, urls, fetchSettings, settingsLoadFailed } = this.props;
    const noticeEligible = (Array.isArray(settings.TimeZoneIds) || settingsLoadFailed) && !settings.TimeZone && Object.keys(urls || {}).some(key => urls[key] && urls[key].Registered);
    const zoneIds = settings.TimeZoneIds || [];
    const zoneOptions = zoneIds.map(id => ({ id, supported: isSupportedZone(id) }));
    const selectedZone = zoneChoice || zone || '';
    const start = zoneStart || (settings.HoursAreDefault ? '09:00' : visibleClock(settings.StartingFreeTime, '09:00'));
    const end = zoneEnd || (settings.HoursAreDefault ? '17:00' : visibleClock(settings.EndingFreeTime, '17:00'));
    const weekdays = zoneDays || settings.AvailableWeekDays || [];
    return (
      <div style={{ overflow: 'hidden' }}>
        <div aria-live="polite" className="visually-hidden">{zoneStatus}</div>
        {autoAdoption && autoAdoption.status === 'saved' && <div data-testid="zone-adoption-success" className="zone-adoption-notice">
          <strong>Your working hours now follow {zoneLabel(autoAdoption.zone).split(' – ')[0]}</strong>
          <p>{formatZonePreview(autoAdoption.start, autoAdoption.end, autoAdoption.zone)}</p>
          <Button style={{ minHeight: 44 }} onClick={() => this.setState({ openZoneRequest: openZoneRequest + 1 })}>Change</Button>
          <Button style={{ minHeight: 44 }} onClick={() => this.setState({ autoAdoption: { status: 'dismissed', user: getCalendarUsername() }, zoneStatus: '' })}>Dismiss</Button>
        </div>}
        {noticeEligible && !willAutoAdopt && (!autoAdoption || autoAdoption.status === 'failed') && (collapsed ? <div data-testid="zone-adoption-warning">Your working hours don&apos;t have a time zone yet. <Button data-testid="zone-adoption-review" onClick={this.expandZonePrompt}>Review</Button></div> :
          <div data-testid="zone-adoption-prompt" className="zone-adoption-notice">
            <strong>Keep your working hours right when clocks change</strong>
            <p>Existing appointments won&apos;t move.</p>
            <label htmlFor="zone-adoption-start">Start <input id="zone-adoption-start" data-testid="zone-adoption-start" type="time" value={start} disabled={savingZone} onChange={e => this.setState({ zoneStart: e.target.value })} /></label>
            <label htmlFor="zone-adoption-end">End <input id="zone-adoption-end" data-testid="zone-adoption-end" type="time" value={end} disabled={savingZone} onChange={e => this.setState({ zoneEnd: e.target.value })} /></label>
            <div>{start} – {end}{start > end && ' (next day)'}</div>
            <span><span id="zone-adoption-label">{selectedZone ? 'Time zone' : 'Choose your time zone'}</span> <select aria-labelledby="zone-adoption-label" id="zone-adoption-zone" data-testid="zone-adoption-zone" value={selectedZone} disabled={savingZone} onChange={e => this.setState({ zoneChoice: e.target.value })}>
              {!selectedZone && <option value="">Choose your time zone</option>}
              {[...zoneOptions].sort((a, b) => Number(!a.supported) - Number(!b.supported)).map(({ id, supported }) => <option key={id} value={id} disabled={!supported} title={!supported ? 'Not available in this browser' : undefined}>{zoneLabel(id)}</option>)}
            </select></span>{zone && selectedZone === zone && <small>Suggested from this device</small>}
            {settingsLoadFailed && zoneIds.length === 0 && <div role="alert">We couldn&apos;t load time zones. <Button data-testid="zone-adoption-retry" onClick={fetchSettings}>Retry</Button></div>}
            <fieldset><legend>Working days</legend>{['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day =>
              <label key={day} htmlFor={`zone-day-${day}`}><input id={`zone-day-${day}`} type="checkbox" checked={weekdays.includes(day)} disabled={savingZone} onChange={e => this.setState({ zoneDays: e.target.checked ? [...weekdays, day] : weekdays.filter(value => value !== day) })} />{day}</label>)}</fieldset>
            <div data-testid="zone-adoption-error" aria-live="polite">{zoneError}{zoneConflict && <Button onClick={() => window.location.reload()}>Reload</Button>}</div>
            <Button data-testid="zone-adoption-use" disabled={savingZone || !zoneOptions.some(({ id, supported }) => id === selectedZone && supported)} onClick={this.useBrowserZone}>Confirm working hours</Button>
            <Button data-testid="zone-adoption-dismiss" disabled={savingZone} onClick={this.dismissZonePrompt}>Not now</Button>
          </div>)}
        <NavbarComponent
          openZoneRequest={openZoneRequest}
          calendarView={calendarView}
          switchToDayView={this.switchToDayView}
          switchToWeekView={this.switchToWeekView}
          refreshCalendar={this.refreshCalendar}
          onDateChange={this.onDateChange}
          onWeekChange={this.onWeekChange}
          dayValue={date.format().slice(0, 10)}
          weekValue={date.format().slice(0, 10)}
          goToToday={this.goToToday}
          cssClassName={navbarCssClassName}
        />
        <br />
        <div ref={this.calendarRef} onScroll={this.handleCalendarScroll} >
          {this.getSchedule(calendarView)}
        </div>
      </div>
    );
  }
}

Calendar.propTypes = {
  eventsByDate: PropTypes.shape({}),
  eventsById: PropTypes.shape({}),
  settings: PropTypes.shape({}),
  urls: PropTypes.shape({}),
  externalCalendarSlots: PropTypes.arrayOf(PropTypes.shape({})),
  fetchEvents: PropTypes.func.isRequired,
  updateEvent: PropTypes.func.isRequired,
  deleteEvent: PropTypes.func.isRequired,
  fetchBusySlots: PropTypes.func.isRequired,
  fetchSettings: PropTypes.func.isRequired,
  fetchUrls: PropTypes.func.isRequired,
  setSettings: PropTypes.func.isRequired,
};

Calendar.defaultProps = {
  eventsById: {},
  eventsByDate: {},
  externalCalendarSlots: [],
  settings: {},
  urls: {},
};

const CalendarContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(Calendar);

export { Calendar };
export default CalendarContainer;
